import { Card } from '@chakra-ui/react';
import {
  IAddressSchema,
  IQuoteContactAddressDataSchema,
  LegalEntityAddressFormatEnum,
} from '@monetize/types/app';
import {
  FunctionComponent as FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Controller,
  FormProvider,
  UseFormReturn,
  useForm,
} from 'react-hook-form';
import {
  useGetAccountById,
  useUpdateAccountAddress,
} from '../../../../../api/accountsService';
import { handleApiErrorToast } from '../../../../../api/axios';
import { AccountAddressSelect } from '../../../../../components/Account/AccountAddressSelect';
import { AddressForm } from '../../../../../components/Contacts/AddressForm';
import {
  MBox,
  MButton,
  MCard,
  MCloseButton,
  MFlex,
  MText,
} from '../../../../../components/Monetize';
import { useFlags } from '../../../../../services/launchDarkly';

interface QuoteContactsAddressItemProps {
  label: string;
  subLabel: string;
  isDisabled?: boolean;
  accountId: string;
  addressDataForm: UseFormReturn<IQuoteContactAddressDataSchema>;
  addressFormat?: LegalEntityAddressFormatEnum | null;
  name: 'shippingAddressId' | 'billingAddressId';
}

const QuoteContactsAddressItem: FC<QuoteContactsAddressItemProps> = ({
  label,
  subLabel,
  isDisabled = false,
  accountId,
  addressDataForm,
  addressFormat = LegalEntityAddressFormatEnum.US_FORMAT,
  name,
}: QuoteContactsAddressItemProps) => {
  const { allowEditAccountAddress } = useFlags();
  const { control, setValue, watch } = addressDataForm;
  const { data: accountDetails, refetch: refetchAccountDetails } =
    useGetAccountById(accountId, {
      enabled: !!accountId,
      refetchOnWindowFocus: false,
    });
  const { mutateAsync: doUpdateAccountAddress, isPending: isUpdatingAddress } =
    useUpdateAccountAddress({
      onError: (error) => {
        handleApiErrorToast(error);
      },
    });
  const [isEditMode, setIsEditMode] = useState(false);
  const [isInlineAddressEditMode, setIsInlineAddressEditMode] = useState(false);
  const [pendingAddressType, setPendingAddressType] = useState<
    'billing' | 'shipping' | null
  >(null);
  const addressId = watch(name);
  const isShippingField = name === 'shippingAddressId';
  const selectedAddressType: 'billing' | 'shipping' | null =
    pendingAddressType ||
    (addressId === accountDetails?.billingAddress?.id
      ? 'billing'
      : addressId === accountDetails?.shippingAddress?.id
        ? 'shipping'
        : null);
  const selectedAddress: IAddressSchema | null =
    selectedAddressType === 'billing'
      ? (accountDetails?.billingAddress ?? null)
      : selectedAddressType === 'shipping'
        ? (accountDetails?.shippingAddress ?? null)
        : null;
  const addressForm = useForm<{ address: IAddressSchema }>({
    mode: 'onChange',
    defaultValues: {
      address: selectedAddress || {},
    },
  });
  useEffect(() => {
    addressForm.reset({
      address: selectedAddress || {},
    });
  }, [selectedAddress, addressForm]);
  const inlineAddressCtaText =
    selectedAddress && selectedAddress.id
      ? 'Edit Address'
      : `Add ${isShippingField ? 'Shipping' : 'Billing'} Address`;
  useEffect(() => {
    if (!addressId) {
      setIsEditMode(true);
    }
  }, [addressId]);
  const selectRef = useRef<{ focus?: () => void } | null>(null);
  useEffect(() => {
    if (isEditMode && selectRef.current) {
      selectRef.current.focus?.();
    }
  }, [isEditMode, selectRef]);
  const canEditInlineAddress =
    allowEditAccountAddress && !isDisabled && !!selectedAddressType;

  const saveInlineAddress = useCallback(
    async (address: IAddressSchema) => {
      if (!selectedAddressType) {
        return;
      }

      const updatedAccount = await doUpdateAccountAddress({
        accountId,
        type: selectedAddressType,
        data: {
          line1: address.line1 ?? null,
          line2: address.line2 ?? null,
          city: address.city ?? null,
          state: address.state ?? null,
          postalCode: address.postalCode ?? null,
          country: address.country ?? null,
          description: address.description ?? null,
        },
      });

      const refreshedAccount = (await refetchAccountDetails()).data;
      const updatedAddressId =
        selectedAddressType === 'billing'
          ? refreshedAccount?.billingAddress?.id ||
            updatedAccount?.billingAddress?.id
          : refreshedAccount?.shippingAddress?.id ||
            updatedAccount?.shippingAddress?.id;

      if (updatedAddressId) {
        setValue(name, updatedAddressId, { shouldDirty: true });
      }

      setPendingAddressType(null);
      addressForm.reset({ address }, { keepValues: true });
    },
    [
      accountId,
      addressForm,
      doUpdateAccountAddress,
      name,
      refetchAccountDetails,
      selectedAddressType,
      setValue,
    ],
  );

  return (
    <MCard variant="borderless" mb="2">
      <Card.Body py="2">
        <MFlex direction="column" grow={1}>
          <MText fontSize="sm" fontWeight="600" mb="4">
            {label}
          </MText>

          <Controller
            name={name}
            control={control}
            render={({ field: { onChange, ...rest } }) => {
              return (
                <AccountAddressSelect
                  isDisabled={isDisabled}
                  isReadOnly={isDisabled || !isEditMode}
                  addressFormat={addressFormat}
                  accountId={accountId}
                  addressType={isShippingField ? 'shipping' : 'billing'}
                  onChange={(value) => {
                    if (value === 'billing' || value === 'shipping') {
                      setPendingAddressType(value);
                      setIsEditMode(false);
                      setIsInlineAddressEditMode(true);
                      return;
                    }
                    setPendingAddressType(null);
                    onChange(value);
                    setIsEditMode(false);
                  }}
                  {...rest}
                  ref={selectRef}
                />
              );
            }}
          />
          {!isDisabled && !isEditMode && (
            <MBox>
              <MButton
                size="xs"
                ml={-2}
                p={2}
                variant="tertiary"
                onClick={() => setIsEditMode(true)}
              >
                Select
              </MButton>
            </MBox>
          )}
          {canEditInlineAddress && !isEditMode && !isInlineAddressEditMode && (
            <MBox mt={1}>
              <MButton
                size="xs"
                ml={-2}
                p={2}
                variant="tertiary"
                onClick={() => setIsInlineAddressEditMode(true)}
              >
                {inlineAddressCtaText}
              </MButton>
            </MBox>
          )}
          {canEditInlineAddress && !isEditMode && isInlineAddressEditMode && (
            <MBox mt={2}>
              <MFlex justifyContent="end" alignItems="center" mb={1}>
                <MCloseButton
                  onClick={() => {
                    addressForm.reset({ address: selectedAddress || {} });
                    setPendingAddressType(null);
                    setIsInlineAddressEditMode(false);
                  }}
                />
              </MFlex>
              <FormProvider {...addressForm}>
                <AddressForm formKeyPrefix="address." />
              </FormProvider>
              <MFlex justifyContent="flex-end" mt={2}>
                <MButton
                  size="sm"
                  variant="primary"
                  isLoading={isUpdatingAddress}
                  onClick={addressForm.handleSubmit(async ({ address }) => {
                    await saveInlineAddress(address);
                    setIsInlineAddressEditMode(false);
                  })}
                >
                  Update
                </MButton>
              </MFlex>
            </MBox>
          )}
        </MFlex>
      </Card.Body>
    </MCard>
  );
};

export default QuoteContactsAddressItem;
